/**
 * Single source of truth for icons across the studio.
 * Wraps lucide-react with our defaults (14px, stroke 1.6) so callers don't
 * have to repeat them and so we can swap pack later by editing one file.
 */
import {
  ChevronLeft as LChevronLeft,
  ChevronRight as LChevronRight,
  ChevronDown as LChevronDown,
  ChevronUp as LChevronUp,
  ArrowRight as LArrowRight,
  ArrowUpDown as LArrowUpDown,
  ArrowUpNarrowWide as LArrowUpNarrowWide,
  ArrowDownWideNarrow as LArrowDownWideNarrow,
  Plus as LPlus,
  Minus as LMinus,
  Pencil as LPencil,
  Copy as LCopy,
  Trash2 as LTrash,
  MoreVertical as LMoreVertical,
  MoreHorizontal as LMoreHorizontal,
  GripVertical as LGripVertical,
  Search as LSearch,
  Folder as LFolder,
  Download as LDownload,
  Link as LLink,
  Wand as LWand,
  Sparkles as LSparkles,
  SlidersHorizontal as LSlidersHorizontal,
  ListPlus as LListPlus,
  Clock as LClock,
  Globe as LGlobe,
  Inbox as LInbox,
  Archive as LArchive,
  Lock as LLock,
  FlaskConical as LFlaskConical,
  FileText as LFileText,
  Star as LStar,
  Undo2 as LUndo2,
  Users as LUsers,
  Pin as LPin,
  Tag as LTag,
  Rows3 as LRows3,
  List as LList,
  Bookmark as LBookmark,
  Info as LInfo,
  TriangleAlert as LTriangleAlert,
  Paperclip as LPaperclip,
  Sun as LSun,
  Moon as LMoon,
  LayoutGrid as LLayoutGrid,
  Library as LLibrary,
  CheckSquare as LCheckSquare,
  ZoomIn as LZoomIn,
  Maximize2 as LMaximize2,
  ArrowUp as LArrowUp,
  Square as LSquare,
  RotateCw as LRotateCw,
  X as LX,
  Check as LCheck,
  PictureInPicture2 as LPictureInPicture2,
  Settings as LSettings,
} from 'lucide-react';
import type { LucideIcon, LucideProps } from 'lucide-react';

const DEFAULTS = { size: 14, strokeWidth: 1.6, 'aria-hidden': true };

const make = (Icon: LucideIcon) => (props: LucideProps) => <Icon {...DEFAULTS} {...props} />;

/** GitHub brand mark — kept as a local custom because lucide v1 removed
 *  brand icons over Simple Icons licensing. Filled mark, scales via `size`. */
export function Github({ size = 14, ...rest }: LucideProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="currentColor" aria-hidden="true" {...rest}>
      <path d="M8 1.5a6.5 6.5 0 0 0-2.05 12.66c.32.06.44-.14.44-.31v-1.2c-1.81.39-2.19-.86-2.19-.86-.3-.74-.72-.94-.72-.94-.59-.4.04-.4.04-.4.65.05 1 .67 1 .67.58 1 1.52.7 1.89.54.06-.42.23-.7.41-.86-1.44-.16-2.96-.72-2.96-3.21 0-.71.25-1.29.67-1.74-.07-.17-.29-.84.06-1.74 0 0 .54-.18 1.78.66a6.1 6.1 0 0 1 3.24 0c1.24-.84 1.78-.66 1.78-.66.35.9.13 1.57.06 1.74.42.45.66 1.03.66 1.74 0 2.5-1.52 3.05-2.97 3.21.24.2.45.61.45 1.23v1.83c0 .17.11.37.44.31A6.5 6.5 0 0 0 8 1.5z" />
    </svg>
  );
}

/** Two streams converging into one — the "merge slides" verb. Local custom
 *  rather than a lucide import: the pack's merge marks are git-flavoured
 *  (branch dots), which reads wrong next to Split/Delete in a slide menu. */
export function Merge({ size = 14, ...rest }: LucideProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      <path d="M6 3v6l6 4" />
      <path d="M18 3v6l-6 4" />
      <path d="M12 13v8" />
      <path d="M9 18l3 3 3-3" />
    </svg>
  );
}

export const ChevronLeft = make(LChevronLeft);
export const ChevronRight = make(LChevronRight);
export const ChevronDown = make(LChevronDown);
export const ChevronUp = make(LChevronUp);
export const ArrowRight = make(LArrowRight);
export const ArrowUpDown = make(LArrowUpDown);
/** The two directions a list can be ordered in. The arrow and the bar lengths
 *  both flip, so which one is showing survives a glance rather than needing the
 *  other one beside it to compare against. */
export const SortAsc = make(LArrowUpNarrowWide);
export const SortDesc = make(LArrowDownWideNarrow);
export const Plus = make(LPlus);
export const Minus = make(LMinus);
export const Pencil = make(LPencil);
export const Copy = make(LCopy);
export const Trash = make(LTrash);
export const Kebab = make(LMoreVertical);
export const KebabHorizontal = make(LMoreHorizontal);
export const Drag = make(LGripVertical);
export const Search = make(LSearch);
export const Folder = make(LFolder);
export const Download = make(LDownload);
export const Link = make(LLink);
export const Wand = make(LWand);
/** The build-provenance facet — same mark the new-deck wizard uses for models. */
export const Sparkles = make(LSparkles);
/** "More filters" — the whole facet sheet, not one facet. */
export const Sliders = make(LSlidersHorizontal);
export const ListPlus = make(LListPlus);
export const Clock = make(LClock);
export const Globe = make(LGlobe);
export const Inbox = make(LInbox);
export const Archive = make(LArchive);
export const Lock = make(LLock);
export const Flask = make(LFlaskConical);
export const FileText = make(LFileText);
/** Starred — the dashboard's pin. Filled by CSS when the deck is starred. */
export const Star = make(LStar);
/** Restore, from the Trash. */
export const Undo = make(LUndo2);
/** The people axis. */
export const Users = make(LUsers);
/** "Set as default" — where a new deck lands. */
export const Pin = make(LPin);
/** The tag axis. */
export const Tag = make(LTag);
/** The rows density — one deck per horizontal strip. */
export const Rows = make(LRows3);
/** The list density — the dense sortable table. */
export const List = make(LList);
/** A saved view. */
export const Bookmark = make(LBookmark);
export const Paperclip = make(LPaperclip);
export const Sun = make(LSun);
export const Moon = make(LMoon);
export const LayoutGrid = make(LLayoutGrid);
export const LibraryIcon = make(LLibrary);
export const CheckSquare = make(LCheckSquare);
export const ZoomIn = make(LZoomIn);
export const Maximize = make(LMaximize2);
/** The composer's send / stop pair. */
export const ArrowUp = make(LArrowUp);
export const StopSquare = (props: LucideProps) => <LSquare {...DEFAULTS} fill="currentColor" {...props} />;
/** Scope markers: one slide vs the whole deck. */
export const Square = make(LSquare);
export const Refresh = make(LRotateCw);
export const Info = make(LInfo);
/** Something went wrong and the user has to decide what to do about it — a
 *  failed import on its shelf tile, not a passing toast. */
export const Warn = make(LTriangleAlert);
/** Close. The one mark this file was missing, which is why sixteen dismiss
 *  buttons across the modals were a literal "✕" character — including two that
 *  sat four lines from a real `<Maximize />` in the same toolbar. */
export const X = make(LX);
/** A tick. Was a literal "✓" wherever something had succeeded or was
 *  already set — including beside the "✕" it is the counterpart to; also the
 *  mark against the chosen row of a listbox. */
export const Check = make(LCheck);
/** Dock a floating panel back to the edge (was a literal "⇲"). */
export const Dock = make(LPictureInPicture2);
/** The app bar's settings cog, which was the "⚙" character on three surfaces. */
export const Settings = make(LSettings);
